import {
  stateForStatus,
  type ActivityState,
  type PimsActivityItem,
} from "@pims/pimsActivityClient";
import {
  Button,
  cn,
  PaperPanel,
  Swipe,
  type SwipeTone,
} from "@scribenote/brian-design-system";
import { formatDateTime } from "@utils/format-date";

/**
 * Each state said in a word, and the highlighter that word wears. The tones are
 * `PimsAudit`'s `STATUS_TONE` table read through this view's coarser vocabulary,
 * so a row and the audit it opens never disagree about what colour a write is:
 * green for work that is moving or landed, red for work that stopped, yellow
 * for work waiting on a person.
 *
 * `tone: null` means the word goes unmarked. `queued` has made no claim yet and
 * `cancelled` never claimed anything, and a wash that says "nothing happened"
 * is a wash with nothing to say. Brian's own blue is deliberately absent: it
 * belongs to the autonomous write modes (see `PimsAudit`'s `WriteMode`), not to
 * every row he files.
 */
const STATE_BADGE: Record<
  ActivityState,
  { label: string; tone: SwipeTone | null }
> = {
  running: { label: "Running", tone: "working" },
  queued: { label: "Queued", tone: null },
  held: { label: "Held", tone: "flag" },
  offline: { label: "Offline", tone: "stopped" },
  done: { label: "Done", tone: "working" },
  failed: { label: "Failed", tone: "stopped" },
  cancelled: { label: "Cancelled", tone: null },
  needs_input: { label: "Needs input", tone: "flag" },
  awaiting_staff: { label: "Waiting on staff", tone: "flag" },
};

/**
 * How this row relates to the other attempt in its retry chain, or null outside
 * one (WEB-3270). Both directions are worth stating: on a failure, that a re-run
 * exists and where it got to — otherwise the row reads as an unaddressed failure
 * forever, since its own status never changes. On the re-run, that it is one, so
 * a second near-identical row in the list isn't mistaken for duplicate work.
 *
 * A row can be both at once, in the middle of a chain (A → B → C).
 *
 * Every field is tested for truthiness, not `!== null`: a response predating
 * these fields leaves them `undefined`, which passes a null check and would put
 * this note — reading "running", from `stateForStatus`'s fallback — on every row
 * in the list, chain or not. Both the linked uuid AND its status are required, so
 * a half-populated link stays silent rather than inventing a state.
 */
const retryChainNote = (item: PimsActivityItem): string | null => {
  const parts: string[] = [];
  if (item.retriedBy && item.retriedByStatus) {
    parts.push(
      `I ran this again. The re-run is ${chainLabel(item.retriedByStatus)}.`,
    );
  }
  if (item.retryOf && item.retryOfStatus) {
    parts.push(
      `This is the re-run of a ${chainLabel(item.retryOfStatus)} attempt.`,
    );
  }
  return parts.length > 0 ? parts.join(" ") : null;
};

/** The other attempt's state as the badges word it, so the two never diverge. */
const chainLabel = (status: string): string =>
  STATE_BADGE[stateForStatus(status)].label.toLowerCase();

/**
 * One row in the PIMS activity list (WEB-3237): a slip of card stock with the
 * instruction at the top, its state marked at the end of that line, and the
 * filing time in mono underneath. Settled history rows are selectable — clicking
 * one loads its replay into the detail pane (`onSelect`), and the currently-open
 * row is framed in ink (`selected`). Live in-flight/queued rows aren't selectable
 * (no replay exists yet) and instead carry their Cancel control. `onCancel` is
 * passed only for cancellable live work and `onRetry` only for failed history
 * rows, so a row never shows both.
 *
 * A selectable row does NOT put button semantics on the card itself, because
 * `onRetry` coexists with `onSelect` and a `<button>` inside a `role="button"`
 * container is an `aria` nested-interactive violation — assistive tech may
 * flatten or omit the inner control, which no amount of event-propagation
 * handling fixes. Instead selection is its own button stretched full-bleed over
 * the card, with the row's action sitting above it: two siblings in the
 * accessibility tree, each separately focusable, whole-card click intact. Its
 * focus ring is drawn inset, since an outset ring on a full-bleed child would be
 * clipped by the sheet it covers.
 */
export const PimsActivityCard = ({
  item,
  state,
  onCancel,
  cancelling = false,
  onRetry,
  retrying = false,
  onSelect,
  selected = false,
}: {
  item: PimsActivityItem;
  state: ActivityState;
  onCancel?: () => void;
  cancelling?: boolean;
  onRetry?: () => void;
  retrying?: boolean;
  onSelect?: () => void;
  selected?: boolean;
}) => {
  const badge = STATE_BADGE[state];
  const chain = retryChainNote(item);

  const card = (
    // data-intercom-target mirrors data-testid, as DSCardWrapper stamped it.
    <PaperPanel
      data-testid={`pims-activity-${item.id}`}
      data-intercom-target={`pims-activity-${item.id}`}
      className={cn(
        "relative min-w-0 p-3",
        // Hover on interactive paper is lift, not recolour. The lift is driven
        // from the selection frame's `group` rather than the sheet's own
        // `:hover`, so pointing anywhere on the row (including at the stretched
        // select button covering it) raises the whole sheet as one object.
        onSelect &&
          "transition duration-150 ease-paper group-hover:-translate-y-px group-hover:shadow-lift-lg motion-reduce:transition-none",
      )}
    >
      {onSelect && (
        <button
          type="button"
          onClick={onSelect}
          aria-label={`Open the replay of: ${item.goal}`}
          data-testid={`pims-activity-select-${item.id}`}
          // Stretched over the card so the whole row stays clickable, and above
          // the (non-interactive) text so a click anywhere lands here. The row's
          // own action sits higher still.
          className="absolute inset-0 z-10 cursor-pointer rounded-paper focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-accent"
        />
      )}
      <div className="flex min-w-0 items-start justify-between gap-2">
        {/* Goals are unbounded free text: the line clamps and the whole chain
            above it is min-w-0, so a long instruction never pushes the state
            marker off the slip. */}
        <p
          className="line-clamp-2 min-w-0 flex-1 text-sm font-medium leading-snug text-ink"
          title={item.goal}
        >
          {item.goal}
        </p>
        <span className="shrink-0 text-xs">
          {badge.tone ? (
            <Swipe
              tone={badge.tone}
              className="text-annotation-caps"
              data-testid={`pims-activity-state-${item.id}`}
            >
              {badge.label}
            </Swipe>
          ) : (
            <span
              className="text-annotation-caps text-annotation"
              data-testid={`pims-activity-state-${item.id}`}
            >
              {badge.label}
            </span>
          )}
        </span>
      </div>
      {chain && (
        <p
          className="mt-1 min-w-0 text-xs text-annotation"
          data-testid={`pims-activity-chain-${item.id}`}
        >
          {chain}
        </p>
      )}
      {/* Attestation (backend BE-2377): a settled staff write says who entered
          it. Gated on the name, not the executor — a still-open human row has
          nothing to attest, and an offboarded completer leaves only the
          timestamp, which the filing line below doesn't cover. */}
      {item.completedByName && (
        <p
          className="mt-1 min-w-0 text-xs text-annotation"
          data-testid={`pims-activity-completed-by-${item.id}`}
        >
          Checked off by {item.completedByName}
          {item.completedAt ? ` · ${formatDateTime(item.completedAt)}` : ""}
        </p>
      )}
      <div className="mt-1.5 flex min-w-0 items-center justify-between gap-2">
        {/* The filing line: when this went in the drawer, in mono, tabular so a
            column of rows lines up digit for digit. */}
        <time
          dateTime={item.createdAt}
          className="block min-w-0 truncate font-mono text-xs tabular-nums text-annotation"
        >
          {formatDateTime(item.createdAt)}
        </time>
        {/* z-20: above the stretched select button, so the action gets its own
            clicks and its own place in the focus order. */}
        {onCancel && (
          <span className="relative z-20">
            <Button
              variant="outline"
              size="sm"
              disabled={cancelling}
              onClick={onCancel}
              data-testid={`pims-activity-cancel-${item.id}`}
            >
              {cancelling ? "Cancelling…" : "Cancel"}
            </Button>
          </span>
        )}
        {onRetry && (
          <span className="relative z-20">
            <Button
              variant="outline"
              size="sm"
              disabled={retrying}
              onClick={onRetry}
              data-testid={`pims-activity-retry-${item.id}`}
            >
              {retrying ? "Retrying…" : "Retry"}
            </Button>
          </span>
        )}
      </div>
    </PaperPanel>
  );

  // Non-selectable (live) rows render bare. Selectable history rows get a
  // 2px frame that's transparent until selected, so the open row is ringed
  // without the list reflowing as selection moves. Dark ink, matching the
  // subject rail's calm selection: the frame says "you are reading this one",
  // which is not a status and takes no status colour.
  if (!onSelect) return card;
  return (
    <div
      className={cn(
        "group rounded-paper border-2",
        selected ? "border-ink" : "border-transparent",
      )}
    >
      {card}
    </div>
  );
};
